import { isArray, numberToCurrency } from '@hc-frontend/utils'
import {
  IS_AGENT,
  IS_BRIDGE_TO_MEDICARE,
  IS_ENROLL,
  MULTI_CARRIER_BROCHURE_LINK,
  MULTI_CARRIER_ENABLED
} from 'core/constants/environment'
import {
  getAllPossibleBenefits,
  getBenefitLabel,
  getBenefits
} from 'core/controllerPlans/getBenefits'
import getMultiCarrierEligibility from 'core/services/getMultiCarrierEligibility'
import additionalTooltips from 'messages/tooltipsAdditional'
import itemTooltips from 'messages/tooltipsPlanType.json'
import getDeductibleOptions from 'sites/pivothealth.com/short-term-health-insurance/consumer/quotes/getDeductibleOptions'
import getPeriodOptions from 'sites/pivothealth.com/short-term-health-insurance/consumer/quotes/getPeriodOptions'
import { filterBenefits } from 'sites/pivothealth.com/short-term-health-insurance/consumer/quotes/renderPlanFilters'
import { DETAILS, ELIGIBILITY, START_APPLICATION } from 'sites/routes'
import setMultiselectOptions from 'utils/DOM/setMultiselectOptions'
import textContent from 'utils/DOM/textContent'
import toggleClass from 'utils/DOM/toggleClass'
import money from 'utils/format/money'
import isBTMPlan from 'utils/pivot/plan/isBTMPlan'
import isRXPlan from 'utils/pivot/plan/isRXPlan'
import { parsePlanSpValue } from '../../registerSnowplowListeners'
import getBTMBenefits from '../renderBTMDetails/getBTMBenefits'
import getById from 'utils/DOM/getById'
import getCoverageLabel from 'utils/plans/getCoverageLabel'

const toMoneyWithoutDecimal = amount => numberToCurrency(amount) || amount
const toMoney = money

let planNameLines = 1

/* eslint-disable complexity */
async function renderPlans(
  node: Element,
  template: Templater,
  state: ComparePage.State
): Promise<void> {
  const {
    plansToShow,
    plansData,
    removedPlans,
    compareBTM,
    filterOptionsByProductType,
    plans,
    plansSTM,
    defaultPlans
  } = state
  const nonVisiblePlans = Object.keys(defaultPlans)
    .filter(planId => !plansToShow.includes(planId))
    .map(planKey => {
      const [planId] = planKey.split('-')
      return plans[planId]
    })
  const planNameHashTable = {}
  const uniqueNonVisiblePlans = nonVisiblePlans.filter(plan => {
    return planNameHashTable[plan.planName]
      ? false
      : (planNameHashTable[`${plan.planName}-${isRXPlan(plan) ? 'rx' : ''}`] = true)
  })

  const plansToRecommend = compareBTM
    ? removedPlans.map(({ id }) => {
        const [planId] = id.split('-')
        return plans[planId]
      })
    : uniqueNonVisiblePlans
        .filter(
          ({ planId }) => !plansToShow.find(planKey => +planKey.split('-')[0] === +planId)
        )
        .map(plan => ({
          ...plan,
          planName: `${plan.planName}${isRXPlan(plan) ? ' | Rx' : ''}`
        }))

  const plansSource = IS_ENROLL ? defaultPlans : plansData

  const planData = await Promise.all(
    plansToShow.map(planId => planMapper(plansSource[planId]))
  )

  const differences = []

  for (
    let attributeIndex = 1;
    attributeIndex < planData[0].length - 1;
    attributeIndex++
  ) {
    const planAttributes = planData.map(
      (plan: ComparePage.PlanTableCellConfig[]): ComparePage.PrimaryValueOptions =>
        plan[attributeIndex].primaryValue
    )
    const rawPlanAttributes = planAttributes.map(
      (attribute: ComparePage.PrimaryValueOptions): string =>
        typeof attribute === 'object' ? attribute.icon : attribute
    )
    if (!rawPlanAttributes.every((attr, i, arr) => attr === arr[0])) {
      differences.push(attributeIndex)
    }
  }

  for (let i = 0; i < planData.length; i++) {
    const planName = String(planData[i][0].primaryValue)
    const planLines = Math.ceil(planName.length / 20)
    planNameLines = planLines >= planNameLines ? planNameLines + 1 : planNameLines
  }

  const viewDetailsHref = IS_ENROLL ? START_APPLICATION : DETAILS
  const selectPlanHref = IS_ENROLL ? START_APPLICATION : ELIGIBILITY

  const planIds = plansToShow.map((planString: string) =>
    parseInt(planString.split('-')[0])
  )
  const selectedPlans = planIds.map((id: number) => state.plans[id])
  const isMultiCarrier = selectedPlans.map(plan => plan?.multiCarrierPlan).includes(true)

  const isEditable = !IS_ENROLL && !isMultiCarrier

  const searchParams = new URLSearchParams(window.location.search)
  const monthsValue = searchParams.get('months')

  let rawPlanData = plansToShow.map(id => plansData[id])

  // If we are on the multi-carrier compare page on the agent site, swap out the brochure links for the 1-3-12 brochure
  if (isMultiCarrier && IS_AGENT) {
    rawPlanData = rawPlanData.map(plan => {
      return {
        ...plan,
        mcBrochure: MULTI_CARRIER_BROCHURE_LINK
      }
    })
  }

  node.innerHTML = template(
    {
      planData,
      totalRows: Object.keys(planData[0]).length,
      viewDetailsHref,
      monthsValue,
      selectPlanHref,
      rawPlanData,
      differences: !isMultiCarrier ? differences : [],
      plansToRecommend,
      planNameLines,
      isEditable,
      isMultiCarrier,
      IS_AGENT: IS_AGENT
    },
    {
      planNameParser: plan => {
        const truncatedName = plan.planName.split(' ').slice(0, 3).join(' ')
        return truncatedName + (isRXPlan(plan) ? ' | Rx' : '')
      },
      parsePlanSpValue
    }
  )

  if (isEditable && !isMultiCarrier) {
    plansToShow.forEach((planKey, index) => {
      const plan = plansData[planKey]
      const planId = planKey.split('-')[0]

      const periodOptions = getPeriodOptions({
        plan: plansData[planKey],
        filterOptions: filterOptionsByProductType[plan.planTypeKey],
        filters: { period: plan.period, policies: plan.policies }
      })
      setMultiselectOptions({
        options: periodOptions.options,
        parentId: `${index}-period-filter`,
        values: periodOptions.values,
        cssClass: 'js-option plan__extend-input'
      })

      const deductibleOptions = getDeductibleOptions({
        plans,
        plan: plans[planId],
        plansSTM: plansSTM,
        benefits: filterBenefits,
        filterOptions: filterOptionsByProductType[plan.planTypeKey]
      })

      setMultiselectOptions({
        options: deductibleOptions.options,
        availableOptions: deductibleOptions.availableOptions,
        parentId: `${index}-deductible-filter`,
        values: [plan.coreBenefits.deductible],
        cssClass: 'js-option'
      })
    })
  }

  toggleClass(
    document.body,
    'plan-removed',
    !!(plansToShow.length !== 3 && removedPlans.length > 0)
  )
  textContent('.js-plan-count', planData.length)
  textContent('.js-enrollment-fee', toMoney(plansData[plansToShow[0]].fee))
  toggleClass('#footer-details-link', 'd-none', !IS_AGENT)

  if (isMultiCarrier) {
    textContent('#compare-button-0', 'Coverage Period Months 1-4')
    textContent('#compare-button-1', 'Coverage Period Months 5-8')
    textContent('#compare-button-2', 'Coverage Period Months 9-12')
    textContent('.compare__heading', 'Comparing your plan selections')
    if (IS_AGENT) {
      textContent('.compare__heading', 'Your Plan Selections')
      if (selectedPlans.length === 2) {
        textContent(
          '.compare__subheading',
          'The 1-3-12 solution is 1 enrollment with 2 carriers for up to 8 months of continuous coverage.'
        )
      } else {
        textContent(
          '.compare__subheading',
          'The 1-3-12 solution is 1 enrollment with 3 carriers for up to 12 months of continuous coverage.'
        )
      }
    } else {
      const averageDiv = getById('average')
      let average = 0
      selectedPlans.forEach(plan => {
        average += plan.durations[0].monthly.premium
      })
      average = Math.round((average * 100) / selectedPlans.length) / 100
      if (averageDiv) {
        averageDiv.innerHTML = `${average}/mo`
      }
      textContent('.compare__heading', 'Your 12 month short-term medical coverage')
      toggleClass('.sub-title', 'd-none', false)
      toggleClass('#average', 'd-none', false)
      toggleClass('#averageTitle', 'd-none', false)
      toggleClass('#carrierNumber', 'd-none', false)
      toggleClass('#coverageTitle', 'd-none', true)
    }

    const actionMenu = document.getElementsByClassName('left-aligned-controls')[0]
    const centerActions = document.getElementsByClassName('action')[0]

    if (MULTI_CARRIER_ENABLED) {
      const quoteLink = document.getElementsByClassName('js-back-to')[0]

      const multiCarrierEligibility = await getMultiCarrierEligibility(
        state.hcStorage.location?.state_acronym,
        state.hcStorage.agent_id
      )

      quoteLink.href = `${quoteLink.href}?months=${multiCarrierEligibility.maxDuration}&maxDuration=${multiCarrierEligibility.maxDuration}`
    }

    if (
      !centerActions.innerHTML.includes('Continue to Enrollment') &&
      !actionMenu.innerHTML.includes('Continue to Enrollment')
    ) {
      const plan = selectedPlans[0]
      // TODO: Replace with the actual durations once we've got multi-carrier plans working on this page
      const durations = 3

      const href = `${ELIGIBILITY}?planid=${plan.planId}&period=120&policies=${durations}`
      if (IS_AGENT) {
        actionMenu.innerHTML = `
          <a
            class="primaryButtonEnroll"
            display="flex"
            id="start-application-from-compare"
            style="margin-bottom: 0px;"
            href="${href}"
          >
            Continue to Enrollment
          </div>
        `
      } else {
        centerActions.innerHTML = `
          <a
            class="primaryButtonEnroll"
            display="flex"
            id="start-application-from-compare"
            style="margin: auto;"
            href="${href}"
          >
            Continue to Enrollment
          </div>
        `
      }
    }
    if (state.eligibilityMonths === 12) {
      toggleClass('#bannerCompare12Months', 'd-none', true)
    } else if (state.eligibilityMonths === 8) {
      toggleClass('#bannerCompare8Months', 'd-none', true)
    }
  } else {
    if (MULTI_CARRIER_ENABLED) {
      const quoteLink = document.getElementsByClassName('js-back-to')[0]
      const multiCarrierEligibility = await getMultiCarrierEligibility(
        state.hcStorage.location?.state_acronym,
        state.hcStorage.agent_id
      )
      quoteLink.href = `${quoteLink.href}?months=4&maxDuration=${multiCarrierEligibility.maxDuration}`
    }
  }
}

const allBenefits = getAllPossibleBenefits()

function formatERDeductible(description: string): string {
  // if description is just number, format it as currency
  // if it is a text, return as is

  const value = parseInt(description, 10)

  if (value.toString() === description) {
    return toMoneyWithoutDecimal(value)
  }

  return description
}

async function planMapper(
  plan: ComparePage.Plan
): Promise<ComparePage.PlanTableCellConfig[]> {
  const planBenefits = getBenefits(plan)
  const policy = isArray(plan.durations)
    ? plan.durations[0]
    : plan.durations[plan.period][plan.policies]

  const isBTM = isBTMPlan(plan)
  const benefitsToRender = []

  if (isBTM) {
    benefitsToRender.push(...(await getBTMBenefits(plan)))
  } else {
    const parsedBenefits = allBenefits.map(benefit => {
      return {
        underlined: true,
        title: getBenefitLabel(benefit),
        tooltip: additionalTooltips[benefit],
        primaryValue: {
          icon: planBenefits.includes(benefit) ? 'compare-yes' : 'compare-no'
        }
      }
    })
    benefitsToRender.push(...parsedBenefits)
  }

  const moopInfo = {
    '3': '*Includes Coinsurance, Deductible, and Copayments',
    '5': '*Does not include Deductible or Copay',
    '59': '*Does not include Deductible or Copay'
  }

  const coverageItem = {
    title: 'Coverage Duration',
    primaryValue: getCoverageLabel(policy.policyDuration, policy.policyMonthsDuration),
    showIf: !isBTM,
    tooltip: itemTooltips['Coverage Duration'],
    underlined: true,
    multiple: true,
    key: `period-filter`
  }
  const coinsuranceItem = {
    title: 'Coinsurance',
    primaryValue: `${plan.coreBenefits.userCoinsurance}%`,
    tooltip: itemTooltips['Coinsurance'],
    underlined: true
  }

  const items = IS_ENROLL
    ? [coinsuranceItem, coverageItem]
    : [coverageItem, coinsuranceItem]

  return [
    // plan name
    {
      primaryValue: plan.planName + (isRXPlan(plan) ? ' | Rx' : '')
    },
    // deductible
    {
      title: `Individual Deductible ${IS_BRIDGE_TO_MEDICARE ? ' †' : ''}`,
      primaryValue: toMoneyWithoutDecimal(+plan.coreBenefits.deductible),
      tooltip: itemTooltips['Deductible'],
      underlined: true,
      classes: 'bt-md-0',
      multiple: true,
      key: `deductible-filter`
    },
    ...items,
    // max policy coverage
    {
      title: 'Total Policy Coverage',
      primaryValue: toMoneyWithoutDecimal(+plan.coreBenefits.lifetimeMax),
      showIf: !isBTM,
      tooltip: itemTooltips['Total Policy Coverage'],
      underlined: true
    },
    // max oop
    {
      title: 'Max Out-of-Pocket',
      primaryValue: toMoneyWithoutDecimal(+plan.coreBenefits.outOfPocketMax),
      tooltip: itemTooltips['Max Out of Pocket'],
      underlined: true,
      info: plan.multiCarrierPlan ? moopInfo[plan.carrierId] : undefined
    },
    // rx deductible
    {
      title: 'Rx Coverage',
      primaryValue: plan.coreBenefits.rxScheduleText,
      tooltip: itemTooltips['Rx Coverage'],
      underlined: true
    },
    // render all benefits
    ...benefitsToRender,
    // ER Deductible
    {
      title: 'ER Deductible',
      primaryValue: formatERDeductible(plan.coreBenefits.emergencyRoomCopay)
    },
    // network
    {
      title: 'Network Type',
      primaryValue: plan.coreBenefits.network
    },
    // provider
    {
      title: 'Network Provider',
      primaryValue: plan.coreBenefits.networkProvider?.replace(/-/g, ' '),
      classes: 'text-capitalize'
    },
    // Price
    {
      primaryValue: plan.monthlyPremium,
      dataPayload: {
        clickTrackingParams: `${plan.planId}-${plan.period}-${plan.policies}`,
        planUrl: plan.planAsURL,
        network:
          plan.coreBenefits.networkProvider === 'cigna'
            ? `${plan.coreBenefits.networkProvider} ${plan.coreBenefits.network}`
            : ''
      }
    }
  ]
}

export default renderPlans
